import {
	Badge,
	Button,
	Checkbox,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
	Input,
	Separator,
	Skeleton,
} from "@ncoder/ui";
import {
	AlertTriangle,
	CheckCircle2,
	ChevronLeft,
	Copy,
	Download,
	Layers,
	Loader2,
	Play,
	Plus,
	Search,
	Terminal,
	Trash2,
	XCircle,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { usePlans } from "@/hooks/testing/usePlans";
import {
	createSuite,
	deleteSuite,
	downloadJunitReport,
	fetchSuite,
	fetchSuiteDiff,
	fetchSuiteRun,
	fetchSuiteRunJunit,
	fetchSuites,
	runSuite,
	type SuiteDiff,
	type SuiteRunStatus,
	type TestPlan,
	type TestSuite,
	type TestSuiteRun,
	updateSuite,
} from "@/services/testing";

const validatedAt = (p: TestPlan): string | null | undefined =>
	(p as { validated_at?: string | null }).validated_at;

type View = { k: "list" } | { k: "create" } | { k: "detail"; id: string };

/**
 * Regression suites: save author-once scenarios into a named suite and re-run them as one unit —
 * e.g. against the preview sandbox before a seasonal Salesforce release — then read a "what newly
 * broke / newly passed" diff. Backed by /api/suites.
 */
export function SuitesDialog({ trigger }: { trigger?: ReactNode } = {}) {
	const [open, setOpen] = useState(false);
	const [view, setView] = useState<View>({ k: "list" });
	const [suites, setSuites] = useState<TestSuite[] | null>(null);
	const plansQ = usePlans();

	async function refresh() {
		setSuites(await fetchSuites().catch(() => []));
	}
	useEffect(() => {
		if (open && suites === null) void refresh();
	}, [open, suites]);

	return (
		<Dialog
			open={open}
			onOpenChange={(o) => {
				setOpen(o);
				if (o) setView({ k: "list" });
			}}
		>
			{/* Callers that need a different affordance (e.g. a sidebar row) pass their own trigger
			    rather than a second copy of this dialog being built around it. */}
			<DialogTrigger asChild>
				{trigger ?? (
					<Button variant="outline" size="sm" className="gap-1.5 text-xs">
						<Layers className="h-3.5 w-3.5" />
						Suites
					</Button>
				)}
			</DialogTrigger>
			<DialogContent className="flex max-h-[85vh] max-w-2xl flex-col gap-0 overflow-hidden p-0">
				<DialogHeader className="space-y-1.5 border-b px-5 py-4">
					<DialogTitle className="flex items-center gap-2 text-base">
						<Layers className="h-4 w-4 text-primary" />
						Regression suites
					</DialogTitle>
					<DialogDescription className="text-xs leading-relaxed">
						Save scenarios into a suite and re-run them as one — e.g. against the preview sandbox
						before a Salesforce release — with a "what newly broke / newly passed" diff.
					</DialogDescription>
				</DialogHeader>

				{view.k === "list" && (
					<SuiteList
						suites={suites}
						onNew={() => setView({ k: "create" })}
						onOpen={(id) => setView({ k: "detail", id })}
					/>
				)}
				{view.k === "create" && (
					<SuiteCreate
						plans={plansQ.data ?? []}
						loading={plansQ.isLoading}
						onCancel={() => setView({ k: "list" })}
						onCreated={(id) => {
							void refresh();
							setView({ k: "detail", id });
						}}
					/>
				)}
				{view.k === "detail" && (
					<SuiteDetail
						suiteId={view.id}
						plans={plansQ.data ?? []}
						onBack={() => {
							void refresh();
							setView({ k: "list" });
						}}
					/>
				)}
			</DialogContent>
		</Dialog>
	);
}

// ── List ──────────────────────────────────────────────────────────────────────

function SuiteList({
	suites,
	onNew,
	onOpen,
}: {
	suites: TestSuite[] | null;
	onNew: () => void;
	onOpen: (id: string) => void;
}) {
	return (
		<>
			<div className="flex items-center justify-between border-b px-5 py-3">
				<span className="text-xs text-muted-foreground">
					{suites ? `${suites.length} suite${suites.length === 1 ? "" : "s"}` : "…"}
				</span>
				<Button size="sm" onClick={onNew} className="h-8 gap-1.5 text-xs">
					<Plus className="h-3.5 w-3.5" /> New suite
				</Button>
			</div>
			<div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto px-5 py-3">
				{suites === null ? (
					[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)
				) : suites.length === 0 ? (
					<div className="flex flex-col items-center gap-2 rounded-lg border border-dashed py-12 text-center">
						<Layers className="h-7 w-7 text-muted-foreground/50" />
						<p className="text-sm font-medium">No suites yet</p>
						<p className="max-w-sm text-xs text-muted-foreground">
							Group your passing scenarios into a suite you can re-run in one click before every
							release.
						</p>
					</div>
				) : (
					suites.map((s) => (
						<button
							key={s.id}
							type="button"
							onClick={() => onOpen(s.id)}
							className="flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-left transition-colors hover:bg-muted/50"
						>
							<div className="min-w-0">
								<div className="truncate text-sm font-medium">{s.name}</div>
								{s.description && (
									<div className="truncate text-xs text-muted-foreground">{s.description}</div>
								)}
							</div>
							<Badge variant="secondary" className="shrink-0 text-[11px]">
								{s.plan_ids.length} scenario{s.plan_ids.length === 1 ? "" : "s"}
							</Badge>
						</button>
					))
				)}
			</div>
		</>
	);
}

// ── Create ──────────────────────────────────────────────────────────────────────

function SuiteCreate({
	plans,
	loading,
	onCancel,
	onCreated,
}: {
	plans: TestPlan[];
	loading: boolean;
	onCancel: () => void;
	onCreated: (id: string) => void;
}) {
	const [name, setName] = useState("");
	const [selected, setSelected] = useState<string[]>([]);
	const [saving, setSaving] = useState(false);

	async function create() {
		if (!name.trim() || selected.length === 0) return;
		setSaving(true);
		try {
			const suite = await createSuite({ name: name.trim(), plan_ids: selected });
			onCreated(suite.id);
		} finally {
			setSaving(false);
		}
	}

	return (
		<>
			<div className="flex items-center gap-2 border-b px-5 py-3">
				<Button variant="ghost" size="sm" onClick={onCancel} className="h-8 gap-1 px-2 text-xs">
					<ChevronLeft className="h-4 w-4" /> Back
				</Button>
				<Input
					value={name}
					onChange={(e) => setName(e.target.value)}
					placeholder="Suite name — e.g. Winter '26 critical paths"
					className="h-9 flex-1 text-sm"
				/>
				<Button
					size="sm"
					onClick={create}
					disabled={saving || !name.trim() || selected.length === 0}
					className="h-9 shrink-0 gap-1.5"
				>
					{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Create"}
				</Button>
			</div>
			<ScenarioPicker plans={plans} loading={loading} selected={selected} onChange={setSelected} />
			<div className="border-t px-5 py-2 text-xs text-muted-foreground">
				{selected.length} scenario{selected.length === 1 ? "" : "s"} selected
			</div>
		</>
	);
}

/** Searchable checklist of the org's scenarios — validated ones first (safest for regression). */
function ScenarioPicker({
	plans,
	loading,
	selected,
	onChange,
}: {
	plans: TestPlan[];
	loading: boolean;
	selected: string[];
	onChange: (next: string[]) => void;
}) {
	const [q, setQ] = useState("");
	const sel = new Set(selected);
	const filtered = useMemo(() => {
		const ql = q.trim().toLowerCase();
		const rows = plans.filter((p) => !ql || (p.name ?? "").toLowerCase().includes(ql));
		// Validated / recently-passing scenarios first — the ones worth regressing.
		return rows.sort(
			(a, b) => Number(!!validatedAt(b)) - Number(!!validatedAt(a)) || (a.name ?? "").localeCompare(b.name ?? ""),
		);
	}, [plans, q]);

	function toggle(id: string) {
		const next = new Set(sel);
		if (next.has(id)) next.delete(id);
		else next.add(id);
		onChange([...next]);
	}

	return (
		<>
			<div className="border-b px-5 py-2.5">
				<div className="relative">
					<Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
					<Input
						value={q}
						onChange={(e) => setQ(e.target.value)}
						placeholder="Search scenarios…"
						className="h-8 pl-8 text-sm"
					/>
				</div>
			</div>
			<div className="min-h-0 flex-1 overflow-y-auto px-2 py-1">
				{loading ? (
					<div className="space-y-1 px-1">
						{[0, 1, 2, 3].map((i) => (
							<Skeleton key={i} className="h-8 w-full rounded-md" />
						))}
					</div>
				) : filtered.length === 0 ? (
					<p className="px-3 py-10 text-center text-xs text-muted-foreground">
						{plans.length === 0 ? "No scenarios yet — author some in chat first." : "No matches."}
					</p>
				) : (
					filtered.map((p) => (
						<button
							key={p.id}
							type="button"
							onClick={() => toggle(p.id)}
							className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors ${
								sel.has(p.id) ? "bg-muted/70" : "hover:bg-muted/50"
							}`}
						>
							<Checkbox checked={sel.has(p.id)} className="pointer-events-none h-3.5 w-3.5" aria-hidden />
							<span className="truncate">{p.name ?? "Untitled scenario"}</span>
							{validatedAt(p) && (
								<Badge className="ml-auto shrink-0 bg-success/15 px-1 py-0 text-[11px] font-medium text-success-strong hover:bg-success/15">
									verified
								</Badge>
							)}
						</button>
					))
				)}
			</div>
		</>
	);
}

// ── Detail (run + diff + history) ────────────────────────────────────────────────

function SuiteDetail({
	suiteId,
	plans,
	onBack,
}: {
	suiteId: string;
	plans: TestPlan[];
	onBack: () => void;
}) {
	const [suite, setSuite] = useState<TestSuite | null>(null);
	const [runs, setRuns] = useState<TestSuiteRun[]>([]);
	const [diff, setDiff] = useState<SuiteDiff | null>(null);
	const [latest, setLatest] = useState<TestSuiteRun | null>(null);
	const [running, setRunning] = useState(false);
	const [editing, setEditing] = useState(false);

	async function load() {
		const [s, d] = await Promise.all([
			fetchSuite(suiteId).catch(() => null),
			fetchSuiteDiff(suiteId).catch(() => null),
		]);
		if (s) {
			setSuite(s.suite);
			setRuns(s.runs);
		}
		if (d) {
			setDiff(d.diff);
			setLatest(d.latest);
		}
	}
	useEffect(() => {
		void load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [suiteId]);

	async function run() {
		if (!suite || suite.plan_ids.length === 0) return;
		setRunning(true);
		try {
			const { suite_run_id } = await runSuite(suiteId);
			// Poll until the background run finishes.
			for (let i = 0; i < 300; i++) {
				await new Promise((r) => setTimeout(r, 4000));
				const r = await fetchSuiteRun(suite_run_id).catch(() => null);
				if (r && r.status !== "running") break;
			}
			await load();
		} finally {
			setRunning(false);
		}
	}

	async function saveScenarios(planIds: string[]) {
		const updated = await updateSuite(suiteId, { plan_ids: planIds });
		setSuite(updated);
		setEditing(false);
	}

	async function remove() {
		await deleteSuite(suiteId).catch(() => {});
		onBack();
	}

	if (editing && suite) {
		return (
			<EditScenarios
				plans={plans}
				initial={suite.plan_ids}
				onCancel={() => setEditing(false)}
				onSave={saveScenarios}
			/>
		);
	}

	return (
		<>
			<div className="flex items-center gap-2 border-b px-5 py-3">
				<Button variant="ghost" size="sm" onClick={onBack} className="h-8 gap-1 px-2 text-xs">
					<ChevronLeft className="h-4 w-4" /> Suites
				</Button>
				<span className="truncate text-sm font-medium">{suite?.name ?? "…"}</span>
				<div className="ml-auto flex items-center gap-1.5">
					<Button
						variant="ghost"
						size="sm"
						onClick={() => setEditing(true)}
						className="h-8 px-2 text-xs"
						disabled={!suite}
					>
						Edit
					</Button>
					<Button
						variant="ghost"
						size="sm"
						onClick={remove}
						className="h-8 px-2 text-xs text-destructive hover:text-destructive"
						title="Delete suite"
					>
						<Trash2 className="h-3.5 w-3.5" />
					</Button>
					<Button
						size="sm"
						onClick={run}
						disabled={running || !suite || suite.plan_ids.length === 0}
						className="h-8 gap-1.5 text-xs"
					>
						{running ? (
							<>
								<Loader2 className="h-3.5 w-3.5 animate-spin" /> Running…
							</>
						) : (
							<>
								<Play className="h-3.5 w-3.5" /> Run suite
							</>
						)}
					</Button>
				</div>
			</div>

			<div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
				{running && (
					<div className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-primary">
						<Loader2 className="h-3.5 w-3.5 animate-spin" />
						Running {suite?.plan_ids.length} scenario{suite?.plan_ids.length === 1 ? "" : "s"} against
						your org — this can take a few minutes.
					</div>
				)}

				{latest && <RunSummaryRow run={latest} />}
				{latest && latest.status !== "running" && <CiGateHint suiteId={suiteId} runId={latest.id} />}
				{diff && !diff.noBaseline && <DiffPanel diff={diff} />}
				{diff?.noBaseline && latest && (
					<p className="text-xs text-muted-foreground">
						This is the suite's first run — it becomes the baseline. Run it again after a change to
						see the "what newly broke" diff.
					</p>
				)}

				{!latest && !running && (
					<div className="flex flex-col items-center gap-2 rounded-lg border border-dashed py-10 text-center">
						<Play className="h-6 w-6 text-muted-foreground/50" />
						<p className="text-sm font-medium">Not run yet</p>
						<p className="max-w-sm text-xs text-muted-foreground">
							Hit <span className="font-medium text-foreground">Run suite</span> to execute all{" "}
							{suite?.plan_ids.length} scenarios and capture a baseline.
						</p>
					</div>
				)}

				{runs.length > 0 && (
					<div>
						<Separator className="my-1" />
						<div className="mb-1.5 mt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
							Run history
						</div>
						<div className="space-y-1">
							{runs.slice(0, 10).map((r) => (
								<div
									key={r.id}
									className="flex items-center justify-between gap-2 rounded-md border bg-card/50 px-3 py-1.5 text-xs"
								>
									<span className="text-muted-foreground">
										{new Date(r.created_at).toLocaleString()}
										{r.label ? ` · ${r.label}` : ""}
									</span>
									<span className="flex items-center gap-2">
										{r.status === "running" ? (
											<Badge variant="secondary" className="text-[11px]">
												running
											</Badge>
										) : (
											<>
												<span className="text-muted-foreground">
													<span className="text-success-strong">{r.passed}✓</span>{" "}
													{r.failed > 0 && <span className="text-destructive">{r.failed}✗ </span>}
													{r.blocked > 0 && <span className="text-warning-strong">{r.blocked}⚠ </span>}
													of {r.total}
												</span>
												<JunitDownloadButton runId={r.id} />
											</>
										)}
									</span>
								</div>
							))}
						</div>
					</div>
				)}
			</div>
		</>
	);
}

/** Download the JUnit XML report for one finished run (the exact artifact a CI pipeline consumes). */
function JunitDownloadButton({ runId }: { runId: string }) {
	const [busy, setBusy] = useState(false);
	return (
		<Button
			variant="ghost"
			size="sm"
			disabled={busy}
			title="Download JUnit XML report"
			className="h-6 gap-1 px-1.5 text-[11px] text-muted-foreground hover:text-foreground"
			onClick={async () => {
				setBusy(true);
				try {
					const { xml } = await fetchSuiteRunJunit(runId);
					downloadJunitReport(runId, xml);
				} catch {
					/* surfaced via the disabled flash; a failed fetch just no-ops */
				} finally {
					setBusy(false);
				}
			}}
		>
			{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
			JUnit
		</Button>
	);
}

/**
 * Discoverability nudge: the same suite is enforceable in CI. Shows the one-line command and lets the
 * user grab the latest run's JUnit — so "runs green in the UI" and "gates the deploy" are one click apart.
 */
function CiGateHint({ suiteId, runId }: { suiteId: string; runId: string }) {
	const [copied, setCopied] = useState(false);
	const cmd = `pnpm ci:gate --suite ${suiteId} --policy regression`;
	return (
		<div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-xs">
			<Terminal className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
			<span className="shrink-0 text-muted-foreground">Gate a deploy in CI:</span>
			<code className="min-w-0 flex-1 truncate font-mono text-[11px] text-foreground">{cmd}</code>
			<Button
				variant="ghost"
				size="sm"
				title={copied ? "Copied" : "Copy command"}
				className="h-6 shrink-0 gap-1 px-1.5 text-[11px] text-muted-foreground hover:text-foreground"
				onClick={async () => {
					try {
						await navigator.clipboard.writeText(cmd);
						setCopied(true);
						setTimeout(() => setCopied(false), 1500);
					} catch {
						/* clipboard unavailable — no-op */
					}
				}}
			>
				{copied ? <CheckCircle2 className="h-3 w-3 text-success-strong" /> : <Copy className="h-3 w-3" />}
			</Button>
			<Button
				variant="ghost"
				size="sm"
				title="Download the latest run's JUnit report"
				className="h-6 shrink-0 gap-1 px-1.5 text-[11px] text-muted-foreground hover:text-foreground"
				onClick={async () => {
					try {
						const { xml } = await fetchSuiteRunJunit(runId);
						downloadJunitReport(runId, xml);
					} catch {
						/* no-op on failure */
					}
				}}
			>
				<Download className="h-3 w-3" /> JUnit
			</Button>
		</div>
	);
}

function RunSummaryRow({ run }: { run: TestSuiteRun }) {
	const ok = run.failed === 0 && run.blocked === 0 && run.errored === 0;
	return (
		<div
			className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border p-3 text-xs ${
				ok ? "border-success/40 bg-success/5" : "border-warning/40 bg-warning/5"
			}`}
		>
			<span className="font-semibold">Latest run</span>
			<span className="text-success-strong">{run.passed} passed</span>
			{run.failed > 0 && <span className="text-destructive">{run.failed} failed</span>}
			{run.blocked > 0 && <span className="text-warning-strong">{run.blocked} blocked</span>}
			{run.errored > 0 && <span className="text-muted-foreground">{run.errored} errored</span>}
			<span className="text-muted-foreground">· {run.total} total</span>
		</div>
	);
}

const STATUS_LABEL: Record<SuiteRunStatus, string> = {
	passed: "passed",
	passed_with_retry: "passed (retry)",
	degraded: "degraded",
	blocked: "blocked",
	failed: "failed",
	error: "error",
};

function DiffPanel({ diff }: { diff: SuiteDiff }) {
	const Section = ({
		title,
		entries,
		tone,
		icon,
	}: {
		title: string;
		entries: SuiteDiff["regressed"];
		tone: string;
		icon: React.ReactNode;
	}) => {
		if (entries.length === 0) return null;
		return (
			<div>
				<div className={`mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide ${tone}`}>
					{icon}
					{title} · {entries.length}
				</div>
				<div className="space-y-1">
					{entries.map((e) => (
						<div
							key={e.plan_id}
							className="flex items-center justify-between gap-2 rounded-md border bg-card/50 px-3 py-1.5 text-xs"
						>
							<span className="truncate">{e.plan_name}</span>
							<span className="shrink-0 text-[11px] text-muted-foreground">
								{e.from ? STATUS_LABEL[e.from] : "—"} → {e.to ? STATUS_LABEL[e.to] : "removed"}
							</span>
						</div>
					))}
				</div>
			</div>
		);
	};

	const clean =
		diff.regressed.length === 0 &&
		diff.fixed.length === 0 &&
		diff.stillFailing.length === 0 &&
		diff.added.length === 0 &&
		diff.removed.length === 0;

	return (
		<div className="space-y-3 rounded-lg border p-3">
			<div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
				Release diff — vs. previous run
			</div>
			{clean ? (
				<p className="flex items-center gap-1.5 text-xs text-success-strong">
					<CheckCircle2 className="h-3.5 w-3.5" /> No change since the last run — nothing newly broke.
				</p>
			) : (
				<>
					<Section
						title="Newly broken"
						entries={diff.regressed}
						tone="text-destructive"
						icon={<XCircle className="h-3.5 w-3.5" />}
					/>
					<Section
						title="Newly fixed"
						entries={diff.fixed}
						tone="text-success-strong"
						icon={<CheckCircle2 className="h-3.5 w-3.5" />}
					/>
					<Section
						title="Still failing"
						entries={diff.stillFailing}
						tone="text-warning-strong"
						icon={<AlertTriangle className="h-3.5 w-3.5" />}
					/>
					<Section
						title="Added"
						entries={diff.added}
						tone="text-muted-foreground"
						icon={<Plus className="h-3.5 w-3.5" />}
					/>
					<Section
						title="Removed"
						entries={diff.removed}
						tone="text-muted-foreground"
						icon={<Trash2 className="h-3.5 w-3.5" />}
					/>
				</>
			)}
		</div>
	);
}

function EditScenarios({
	plans,
	initial,
	onCancel,
	onSave,
}: {
	plans: TestPlan[];
	initial: string[];
	onCancel: () => void;
	onSave: (planIds: string[]) => void;
}) {
	const [selected, setSelected] = useState<string[]>(initial);
	const [saving, setSaving] = useState(false);
	return (
		<>
			<div className="flex items-center gap-2 border-b px-5 py-3">
				<Button variant="ghost" size="sm" onClick={onCancel} className="h-8 gap-1 px-2 text-xs">
					<ChevronLeft className="h-4 w-4" /> Cancel
				</Button>
				<span className="text-sm font-medium">Edit scenarios</span>
				<Button
					size="sm"
					onClick={async () => {
						setSaving(true);
						try {
							onSave(selected);
						} finally {
							setSaving(false);
						}
					}}
					disabled={saving || selected.length === 0}
					className="ml-auto h-8 gap-1.5 text-xs"
				>
					{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
				</Button>
			</div>
			<ScenarioPicker plans={plans} loading={false} selected={selected} onChange={setSelected} />
			<div className="border-t px-5 py-2 text-xs text-muted-foreground">
				{selected.length} scenario{selected.length === 1 ? "" : "s"} selected
			</div>
		</>
	);
}
